import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useI18N } from "@sundae/react-hooks";
import { Badge, ConditionalWrapper, ExternalLink, ExternalLinkIcon, FlexList, FlexListItem, InfoCircledIcon, Loader, Notice, Separator, Text, toolkitCx, Tooltip, } from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import orderBy from "lodash/orderBy.js";
import { useCallback, useMemo, useState } from "react";
import { ZEROES } from "../../constants/general.js";
import { useClientBrand } from "../../hooks/useClientBrand.js";
import { useRewardAssetMetadata } from "../../hooks/useRewardAssetMetadata.js";
import { canClaimRewards, rewardClaimed, rewardExpired, rewardOnCooldown, rewardTentative, rewardUnavailable, } from "../../hooks/useRewards.js";
import { useServerTime } from "../../hooks/useTime.js";
import { useAppContext } from "../../stores/index.js";
import { unixToSlot } from "../../utils/slots.js";
import BalanceWithAssetName from "../BalanceWithAssetName/index.js";
import Button from "../Button/Button.js";
import CopyText from "../CopyText/index.js";
import LoadingComponent from "../LoadingComponent/LoadingComponent.js";
const RewardsEstimate = ({ rewards, rewardsMutation, notEnoughADA, claimRewardMutation: { mutateAsync: onClaimRewards, isLoading: isClaiming, isSuccess, }, showMultiClaimWarning, alert, cancelCooldown, displayFooter = false, }) => {
    const { details: { network }, } = useWalletDetails();
    const { buttons: { primary }, } = useClientBrand();
    const { state: { clientSettings: { includeClaimingFee }, }, } = useAppContext();
    const { t } = useI18N("rewards");
    const [claimTx, setClaimTx] = useState("");
    const explorerBase = window.__APP_CONFIG.cardano.transactionExplorerUrl;
    const orderedRewards = useMemo(() => orderBy(rewards, ({ epoch }) => epoch, ["asc"]), [rewards]);
    const { data: orderedMetadata, isLoading } = useRewardAssetMetadata(orderedRewards.map(({ assetId }) => assetId));
    const timeQuery = useServerTime();
    const time = timeQuery?.data?.now ?? new Date().toISOString();
    const canClaim = useMemo(() => {
        return (!cancelCooldown && rewards.some((r) => canClaimRewards(r, time, network)));
    }, [rewards, network, cancelCooldown]);
    const isCooldown = useMemo(() => {
        return rewards.some((r) => rewardOnCooldown(r, time, network));
    }, [rewards, network]);
    const getBadgeType = useCallback((reward) => {
        if (rewardTentative(reward)) {
            return {
                variant: "silent",
                text: t("pills.tentative"),
                tooltip: t("tentative"),
            };
        }
        if (rewardExpired(reward, time)) {
            return {
                variant: "silent",
                text: t("pills.expired"),
            };
        }
        if (rewardUnavailable(reward, time)) {
            return {
                variant: "silent",
                text: t("pills.availableSoon"),
            };
        }
        if (rewardClaimed(reward)) {
            return {
                variant: "success",
                text: t("pills.success"),
            };
        }
        if (reward.paidTxId === "") {
            return {
                variant: "info",
                text: t("pills.unclaimed"),
            };
        }
        const currentSlot = unixToSlot(Math.floor(new Date(time).valueOf() / 1000), network);
        if (reward.paidTxId !== "" && currentSlot < reward?.invalidAt) {
            return {
                variant: "warning",
                text: t("pills.pending"),
            };
        }
        if (reward.paidTxId !== "" &&
            reward?.invalidAt < currentSlot &&
            reward?.invalidAt < reward?.finalAt) {
            return {
                variant: "warning",
                text: t("pills.cooldown"),
            };
        }
        if (reward.paidTxId !== "" && reward.seenTxId === reward.paidTxId) {
            return {
                variant: "success",
                text: t("pills.success"),
            };
        }
        return null;
    }, [network, t]);
    const handleClaim = useCallback(async () => {
        if (!canClaim) {
            return;
        }
        const tx = await onClaimRewards();
        setClaimTx(tx);
        rewardsMutation.refetch();
    }, [rewardsMutation?.refetch, canClaim]);
    const getBadgeElement = useCallback((reward, idx = 0) => {
        const badge = getBadgeType(reward);
        if (!badge) {
            return null;
        }
        const badgeElement = (_jsx(Badge, { variant: badge.variant, size: "xs", className: "w-18 ml-auto", children: badge.text }));
        if (badge.tooltip) {
            return (_jsx(Tooltip, { tooltipLabel: badge.tooltip, trigger: badgeElement }, `${reward.purpose}-${reward.epoch}-${idx}-tooltip`));
        }
        return badgeElement;
    }, []);
    return (_jsxs("div", { className: "pt-6", children: [_jsx("div", { className: "mb-7 grid gap-2", children: orderedRewards.map((reward, idx) => {
                    const { amount, epoch, purpose, seenTxId } = reward;
                    return (_jsxs("div", { className: "grid gap-2", children: [_jsxs("div", { className: "reward-badges flex items-center gap-4", children: [_jsx(Badge, { variant: "gradient", size: "xs", className: "w-18", children: purpose }), getBadgeElement(reward, idx)] }), _jsxs(FlexList, { children: [seenTxId && (_jsx(FlexListItem, { field: {
                                            label: t("seenTxId", {
                                                context: seenTxId === ZEROES ? t("dripdropz") : t("tx"),
                                            }),
                                            size: "sm",
                                            variant: "muted",
                                        }, value: {
                                            label: (_jsx("div", { className: "flex flex-wrap", children: seenTxId === ZEROES ? (_jsxs(ExternalLink, { size: "sm", className: "flex items-center justify-center gap-2", href: `https://dripdropz.io`, children: [t("claimed_dripdropz"), _jsx(ExternalLinkIcon, {})] })) : (_jsxs(_Fragment, { children: [_jsx(CopyText, { tooltipText: seenTxId, visibleText: `${seenTxId.slice(0, 6)}...${seenTxId.slice(6, 12)}` }), _jsx(ExternalLink, { size: "sm", className: "ml-2 flex items-center justify-center gap-2", href: `${explorerBase}/${seenTxId}`, children: _jsx(ExternalLinkIcon, {}) })] })) })),
                                        } })), _jsx(FlexListItem, { field: {
                                            label: t(reward.apr
                                                ? "estimatedRewardsWithAPR"
                                                : "estimatedRewards", {
                                                apr: ((reward.apr ?? 0) * 100).toFixed(2),
                                            }),
                                            size: "sm",
                                            variant: "muted",
                                        }, value: {
                                            label: isLoading ? (_jsx(LoadingComponent, {})) : (_jsx(_Fragment, { children: !orderedMetadata?.[idx] ? (_jsx(Tooltip, { trigger: _jsxs(Text, { tag: "p", children: ["Something went wrong. ", _jsx(InfoCircledIcon, {})] }), tooltipLabel: "We could not fetch assets information for this token." })) : (_jsx(BalanceWithAssetName, { abbreviate: false, asset: {
                                                        ...orderedMetadata[idx],
                                                        ticker: orderedMetadata[idx].assetName,
                                                    }, showSymbol: false, size: "sm", value: new AssetAmount(amount, {
                                                        ...orderedMetadata[idx],
                                                        assetId: orderedMetadata[idx].id,
                                                    }), variant: "muted" })) })),
                                        } }), reward.comment && (_jsx(FlexListItem, { field: {
                                            label: t("comment"),
                                            size: "sm",
                                            variant: "muted",
                                        }, value: {
                                            label: (_jsx(Text, { size: "sm", tag: "p", variant: "muted", children: reward.comment })),
                                        } })), reward.availableAt && time < reward.availableAt && (_jsx(FlexListItem, { field: {
                                            label: t("availableAt"),
                                            size: "sm",
                                            variant: "muted",
                                        }, value: {
                                            label: (_jsx(Text, { size: "sm", tag: "p", variant: "muted", children: new Date(reward.availableAt).toLocaleString() })),
                                        } })), reward.expiresAt && reward.expiresAt < time && (_jsx(FlexListItem, { field: {
                                            label: t("expiration"),
                                            size: "sm",
                                            variant: "muted",
                                        }, value: {
                                            label: (_jsx(Text, { size: "sm", tag: "p", variant: "muted", children: new Date(reward.expiresAt).toLocaleString() })),
                                        } })), _jsx(Separator, { className: toolkitCx.layout.background.separator, orientation: "horizontal" })] })] }, `${purpose}-${epoch}-${idx}`));
                }) }), displayFooter && (_jsxs(_Fragment, { children: [notEnoughADA || !canClaim ? (_jsx(Notice, { className: "m-3", type: "warning", children: canClaim
                            ? t("notEnoughADA")
                            : isCooldown
                                ? t("cooldown")
                                : t("alreadyClaimed") })) : null, _jsxs(ConditionalWrapper, { condition: notEnoughADA || !canClaim, wrapper: (children) => (_jsx(Tooltip, { trigger: _jsx("div", { children: children }), tooltipLabel: canClaim
                                ? t("notEnoughADA")
                                : isCooldown
                                    ? t("cooldown")
                                    : t("alreadyClaimed") })), children: [includeClaimingFee && (_jsx("div", { className: "grid gap-2 py-8 lg:mx-auto lg:w-3/4", children: _jsx(Notice, { type: "success", children: t("fee") }) })), _jsxs(Button, { className: "mx-auto !block", variant: "gradient", ...primary, disabled: notEnoughADA ||
                                    !canClaim ||
                                    isClaiming ||
                                    isSuccess ||
                                    Boolean(claimTx), onClick: handleClaim, children: [isClaiming ? t("claimingRewards") : t("claimRewards"), isClaiming && _jsx(Loader, { size: "xsmall", className: "mx-4" })] }), showMultiClaimWarning && (_jsx(Text, { tag: "p", variant: "muted", size: "sm", className: "mx-auto max-w-[400px] pt-4 text-center", children: t("multiClaimWarning") })), alert && _jsx("div", { className: "mx-auto my-2 lg:w-3/4", children: alert }), claimTx && isSuccess && (_jsx("div", { className: "grid gap-2 py-8 lg:mx-auto lg:w-3/4", children: _jsx(Notice, { type: "success", children: t("claimed") }) }))] })] }))] }));
};
export default RewardsEstimate;
//# sourceMappingURL=RewardsEstimate.js.map